import {Component, Inject, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {DIALOG_DATA, DialogRef} from '@angular/cdk/dialog';
import {Observable} from 'rxjs';

/** One input of the login: a username, a password, a token or a code. */
export interface LoginField {
  name: string;
  label: string;
  type: 'text' | 'password';
  value: string;
  autocomplete: string;
  placeholder?: string;
  monospace?: boolean;
}

/**
 * What one submit came back with. `done` closes the dialog; `retry` keeps the fields and shows the message; `next`
 * shows the message and replaces the fields (a second step, such as the code mailed for an MFA portal).
 */
export interface LoginOutcome {
  status: 'done' | 'retry' | 'next';
  message: string;
  severity: 'info' | 'warning' | 'danger';
  fields?: LoginField[];
}

export interface LoginDialogData {
  headline: string;
  intro?: string;
  fields: LoginField[];
  actionName: string;
  /** Sends the field values; the dialog reads the outcome and shows it. */
  submit: (values: Record<string, string>) => Observable<LoginOutcome>;
}

/**
 * The one login dialog for every portal and endpoint that signs in: the fields are the page's own (a username and
 * password, a token, a code). The dialog closes only through its buttons; a login that succeeds closes it with `true`.
 */
@Component({
  selector: 'app-login-dialog',
  standalone: true,
  imports: [FormsModule],
  template: `
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">{{ data.headline }}</div>
        <div class="modal-body cl-form">
          @if (data.intro) {
            <p class="cl-p-regular">{{ data.intro }}</p>
          }
          @for (field of fields(); track field.name) {
            <div role="group">
              <label class="form-label" [attr.for]="'login-' + field.name">{{ field.label }}</label>
              <input [type]="field.type" class="it-login-field" [class.cl-field-sans]="!field.monospace"
                     [id]="'login-' + field.name" [attr.autocomplete]="field.autocomplete"
                     [attr.placeholder]="field.placeholder ?? null" spellcheck="false"
                     [(ngModel)]="field.value" (keyup.enter)="submit()">
            </div>
          }
          @if (message(); as text) {
            <p class="cl-p-regular" [class.cl-color-brick]="text.severity === 'danger'"
               [class.cl-color-honey]="text.severity === 'warning'">{{ text.text }}</p>
          }
        </div>
        <div class="modal-footer">
          <span class="modal-footer-left">
            <button type="button" class="btn cl-color-secondary it-cancel" (click)="dialogRef.close(false)">
              <span>Cancel</span>
            </button>
          </span>
          <span class="modal-footer-center"></span>
          <span class="modal-footer-right">
            <button type="button" class="btn cl-color-primary it-ok" [class.cl-btn-spinner]="busy()"
                    [disabled]="busy()" (click)="submit()">
              <span>{{ data.actionName }}</span>
            </button>
          </span>
        </div>
      </div>
    </div>
  `,
  styles: ['input { width: 100%; }'],
})
export class LoginDialogComponent {
  fields = signal<LoginField[]>([]);
  busy = signal(false);
  message = signal<{text: string; severity: LoginOutcome['severity']} | null>(null);

  constructor(
    public dialogRef: DialogRef<boolean>,
    @Inject(DIALOG_DATA) public data: LoginDialogData,
  ) {
    this.fields.set(data.fields.map(field => ({...field})));
  }

  submit(): void {
    if (this.busy()) {
      return;
    }
    const current = this.fields();
    if (current.some(field => !field.value)) {
      this.message.set({text: 'fill in every field', severity: 'warning'});
      return;
    }
    const values = Object.fromEntries(current.map(field => [field.name, field.monospace ? field.value.trim() : field.value]));
    this.busy.set(true);
    this.data.submit(values).subscribe({
      next: (outcome) => {
        this.busy.set(false);
        if (outcome.status === 'done') {
          this.dialogRef.close(true);
          return;
        }
        if (outcome.fields) {
          this.fields.set(outcome.fields.map(field => ({...field})));
        }
        this.message.set({text: outcome.message, severity: outcome.severity});
      },
      error: (err) => {
        this.busy.set(false);
        this.message.set({text: err?.message ?? String(err), severity: 'danger'});
      },
    });
  }
}
